<template>
  <v-container max-width="800" class="py-4">

    <v-progress-linear v-if="loading" indeterminate color="primary" rounded height="4" class="mb-4" />

    <v-alert v-if="errorMsg" type="error" variant="tonal" class="mb-4">{{ errorMsg }}</v-alert>

    <div class="d-flex align-center mb-3">
      <v-switch
        v-model="onlyRecalled"
        color="error"
        density="compact"
        hide-details
        class="flex-shrink-0"
      />
      <span class="text-body-2 ml-2">Uniquement les achats avec rappel</span>
    </div>

    <template v-if="!loading && filteredPurchases.length === 0">
      <div class="d-flex flex-column align-center justify-center py-12 text-medium-emphasis">
        <v-icon size="64" class="mb-3">mdi-receipt-text-outline</v-icon>
        <p class="text-body-1">Aucun achat enregistré</p>
        <p class="text-body-2">Scannez un code-barres ou un ticket de caisse et enregistrez-le.</p>
      </div>
    </template>

    <v-list v-else lines="two" class="pa-0 bg-transparent">
      <v-card
        v-for="purchase in filteredPurchases"
        :key="purchase.id"
        class="mb-3 cursor-pointer"
        @click="openDetail(purchase)"
      >
        <div class="d-flex align-center" style="height: 90px">
          <!-- Thumbnail -->
          <div
            class="flex-shrink-0 bg-grey-lighten-3 rounded-s-lg d-flex align-center justify-center overflow-hidden"
            style="width: 90px; height: 90px"
          >
            <v-img
              v-if="purchase.imageFilename"
              :src="`/api/purchases/${purchase.id}/image`"
              cover
              width="90"
              height="90"
            />
            <v-icon v-else size="36" color="grey">
              {{ purchaseIcon(purchase) }}
            </v-icon>
          </div>

          <!-- Info -->
          <div class="d-flex flex-column justify-center pa-3" style="min-width: 0">
            <div class="d-flex align-center flex-wrap ga-1 mb-1">
              <v-chip
                :color="purchase.type === 'barcode' ? 'primary' : purchase.type === 'fidelity' ? 'success' : 'secondary'"
                size="x-small"
                variant="tonal"
              >
                {{ typeLabel(purchase) }}
              </v-chip>
              <v-chip
                v-if="purchase.fidelityCard"
                color="primary"
                size="x-small"
                variant="tonal"
              >
                {{ fidelityCardLabel(purchase.fidelityCard.type) }}
              </v-chip>
              <v-chip
                v-if="recallCount(purchase) > 0"
                color="error"
                size="x-small"
                variant="tonal"
              >
                <v-icon start size="10">mdi-alert</v-icon>
                {{ recallCount(purchase) }} rappel{{ recallCount(purchase) > 1 ? 's' : '' }}
              </v-chip>
            </div>
            <span class="text-body-2 font-weight-medium">
              {{ formatDate(purchase.createdAt) }}
            </span>
            <span class="text-caption text-medium-emphasis mt-1">
              {{ purchase.products?.length ?? 0 }} produit{{ (purchase.products?.length ?? 0) > 1 ? 's' : '' }}
            </span>
          </div>

          <v-icon class="ml-auto mr-3 align-self-center" color="grey-lighten-1">mdi-chevron-right</v-icon>
        </div>
      </v-card>
    </v-list>

    <!-- Detail bottom sheet -->
    <v-bottom-sheet v-model="detailOpen" max-width="640" scrollable>
      <v-card v-if="selected" rounded="t-xl" class="purchase-sheet pa-0">
        <!-- Header -->
        <div class="d-flex align-center pa-4 pb-2">
          <div>
            <p class="text-body-1 font-weight-semibold mb-0">
              {{ typeLabel(selected) }}
            </p>
            <p v-if="selected.fidelityCard" class="text-caption sheet-muted mb-0">
              {{ fidelityCardLabel(selected.fidelityCard.type) }} · {{ selected.fidelityCard.number }}
            </p>
            <p class="text-caption sheet-muted">{{ formatDate(selected.createdAt) }}</p>
          </div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" size="small" color="white" @click="detailOpen = false" />
        </div>

        <!-- Photo -->
        <v-img
          v-if="selected.imageFilename"
          :src="`/api/purchases/${selected.id}/image`"
          max-height="200"
          cover
          class="mx-4 mb-3 rounded-lg"
        />

        <v-divider class="mb-3" />

        <!-- Products -->
        <div class="px-4 pb-4">
          <p class="text-body-2 sheet-muted mb-3">
            Produits ({{ detailProducts.length }})
          </p>

          <template v-if="loadingDetail">
            <v-skeleton-loader v-for="n in 2" :key="n" type="card" class="mb-2" />
          </template>

          <div v-for="product in detailProducts" :key="product.id" class="mb-2">
            <!-- Recalled product — use RecallCard -->
            <recall-card v-if="product.recall" :recall="toRecallItem(product.recall)" />

            <!-- Non-recalled product — simple card -->
            <v-card v-else rounded="lg">
              <div class="d-flex align-center px-3 py-3 ga-3">
                <v-icon color="grey">mdi-barcode</v-icon>
                <div>
                  <p class="text-body-2 font-weight-medium mb-0">{{ product.gtin ?? '—' }}</p>
                  <p class="text-caption text-medium-emphasis mb-0">Aucun rappel connu</p>
                </div>
              </div>
            </v-card>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

  </v-container>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import RecallCard, { type RecallItem } from './RecallCard.vue'
import { fidelityCardLabel } from '../fidelity'

interface RecallSummary {
  id: number
  label: string
  productCategory: string
  recallSheetUrl: string | null
  imageUrls: string | null
  publicationDate: string | null
}

interface ProductSummary {
  id: number
  gtin: string | null
  recall: RecallSummary | null
}

interface FidelityCardSummary {
  id: number
  type: string
  number: string
}

interface Purchase {
  id: number
  type: string
  imageFilename: string | null
  createdAt: string
  products?: ProductSummary[]
  fidelityCard?: FidelityCardSummary | null
}

interface HydraCollection {
  'member': Purchase[]
}

export default defineComponent({
  name: 'PurchasesView',
  components: { RecallCard },

  setup() {
    const purchases     = ref<Purchase[]>([])
    const loading       = ref(false)
    const errorMsg      = ref('')
    const detailOpen    = ref(false)
    const selected      = ref<Purchase | null>(null)
    const loadingDetail = ref(false)

    const onlyRecalled = ref(false)

    const detailProducts = computed<ProductSummary[]>(
      () => (selected.value?.products ?? []) as ProductSummary[]
    )

    const filteredPurchases = computed(() =>
      onlyRecalled.value
        ? purchases.value.filter(p => recallCount(p) > 0)
        : purchases.value
    )

    function typeLabel(purchase: Purchase): string {
      if (purchase.type === 'barcode') return 'Code-barres'
      if (purchase.type === 'receipt') return 'Ticket de caisse'
      if (purchase.type === 'fidelity') return 'Fidélité'
      if (purchase.type === 'invoice') return 'Facture'
      return purchase.type
    }

    function purchaseIcon(purchase: Purchase): string {
      if (purchase.fidelityCard || purchase.type === 'fidelity') return 'mdi-credit-card-outline'
      if (purchase.type === 'barcode') return 'mdi-barcode'
      return 'mdi-receipt-text-outline'
    }

    function recallCount(purchase: Purchase): number {
      return (purchase.products ?? []).filter(p => !!p.recall).length
    }

    function toRecallItem(r: RecallSummary): RecallItem {
      return {
        '@id': `/api/recalls/${r.id}`,
        id: r.id,
        recallGuid: '',
        sheetNumber: '',
        label: r.label,
        productCategory: r.productCategory,
        imageUrls: r.imageUrls,
        recallSheetUrl: r.recallSheetUrl,
        publicationDate: r.publicationDate,
      }
    }

    function formatDate(iso: string): string {
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      }).format(new Date(iso))
    }

    async function fetchPurchases(): Promise<void> {
      loading.value  = true
      errorMsg.value = ''
      try {
        const res = await fetch('/api/purchases', {
          credentials: 'include',
          headers: { Accept: 'application/ld+json' },
        })
        if (!res.ok) throw new Error('Impossible de charger les achats.')
        const data: HydraCollection = await res.json()
        purchases.value = data['member'] ?? []
      } catch (err) {
        errorMsg.value = err instanceof Error ? err.message : 'Une erreur est survenue.'
      } finally {
        loading.value = false
      }
    }

    async function openDetail(purchase: Purchase): Promise<void> {
      selected.value  = purchase
      detailOpen.value = true

      // Fetch full detail if products not yet loaded
      if (!purchase.products) {
        loadingDetail.value = true
        try {
          const res = await fetch(`/api/purchases/${purchase.id}`, {
            credentials: 'include',
            headers: { Accept: 'application/ld+json' },
          })
          if (res.ok) {
            const data: Purchase = await res.json()
            // Merge products into the purchases array
            const idx = purchases.value.findIndex(p => p.id === purchase.id)
            if (idx !== -1) purchases.value[idx] = data
            selected.value = data
          }
        } finally {
          loadingDetail.value = false
        }
      }
    }

    onMounted(fetchPurchases)

    return {
      purchases,
      loading,
      errorMsg,
      detailOpen,
      selected,
      loadingDetail,
      detailProducts,
      filteredPurchases,
      onlyRecalled,
      formatDate,
      openDetail,
      recallCount,
      toRecallItem,
      typeLabel,
      purchaseIcon,
      fidelityCardLabel,
    }
  },
})
</script>
